import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import Modal from 'react-modal';

import {
  showCustomDateRangeModal,
  hideCustomDateRangeModal,
  getMiDashboard,
  resetMiDashboard
} from 'actions';

import MICustomDateRange from 'components/MIDashboard/MICustomDateRange';
import MIResults from 'components/MIDashboard/MiResults';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';

Modal.setAppElement('#root');

class MIDashboard extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    isDateRangePickerShown: PropTypes.bool.isRequired,
    isGettingMiDashboard: PropTypes.bool.isRequired,
    isGettingMiDashboardXls: PropTypes.bool.isRequired,
    dateRangeFrom: PropTypes.string.isRequired,
    dateRangeTo: PropTypes.string.isRequired,
    miData: PropTypes.shape(),
    clients: PropTypes.arrayOf(PropTypes.shape()),
    selectedClient: PropTypes.shape({
      key: PropTypes.number,
      value: PropTypes.string
    }),
    error: PropTypes.shape({
      code: PropTypes.number,
      message: PropTypes.string
    }),
  };

  static defaultProps = {
    miData: null,
    error: null,
    clients: [],
    selectedClient: null
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedDatePeriod: '',
      trendAnalysisType: 'none',
      includeDeletedForms: false,
      clientId: props.selectedClient != null ? props.selectedClient.key : null,
      errorMessage: null,
      disableSubmit: false
    };

    this.initialState = this.state;

    this.applyCustomDateRangeModal = this.applyCustomDateRangeModal.bind(this);
    this.onRunReportClick = this.onRunReportClick.bind(this);
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillReceiveProps(newProps) {
    const { clients, error, miData, dateRangeFrom, dateRangeTo } = this.props;
    const { clientId } = this.state;

    if (clients != null && clients.length === 1 && clientId === null) {
      this.setState({ clientId: clients[0].key });
    }

    if (newProps.error && (error === null || error.code !== newProps.error.code)) {
      this.setState({ errorMessage: newProps.error.message });
    } else if (newProps.miData != null && newProps.miData.total === 0) {
      this.setState({ errorMessage: 'No forms were submitted for validation during this period.' });
    } else {
      this.setState({ errorMessage: null });
    }

    if (new Date(dateRangeFrom) > new Date(dateRangeTo)) {
      this.setState({ errorMessage: 'The "From" date must be before the "To" date.', disableSubmit: true });
    } else {
      this.setState({ disableSubmit: false });
    }

    if (miData != null && newProps.miData == null) {
      this.setState(this.initialState);
    }
  }
  /* eslint-enable camelcase */

  componentWillUnmount() {
    this.resetForm();
  }

  onCloseErrorMessage() {
    this.setState({ errorMessage: null });
  }

  onDatePeriodChange(event) {
    if (event != null && event.target != null && event.target.value === 'custom') {
      this.openCustomDateRangeModal();
    }
    this.setState({ selectedDatePeriod: event.target.value, trendAnalysisType: 'none', errorMessage: null, disableSubmit: false });
  }

  onTrendAnalysisChange(event) {
    this.setState({ trendAnalysisType: event.target.value });
  }

  onIncludeDeleted(event) {
    this.setState({ includeDeletedForms: event.target.checked });
  }

  onSelectedClientChange(event) {
    this.setState({ errorMessage: null, clientId: (event != null && event.target != null && event.target.value !== '') ? parseInt(event.target.value, 10) : null });
  }

  onRunReportClick() {
    const { dispatch, dateRangeFrom, dateRangeTo } = this.props;
    const { selectedDatePeriod, trendAnalysisType, clientId, includeDeletedForms } = this.state;

    let from = moment();
    let to = moment();
    let daystoWeekMonday = 0 - (1 - from.isoWeekday());

    switch (selectedDatePeriod) {
      case 'today':
        break;
      case 'thisweek':
        from = from.subtract(daystoWeekMonday, 'days');
        to = to.subtract(daystoWeekMonday, 'days').add('days', 6);
        break;
      case 'lastweek':
        daystoWeekMonday += 7;
        from = from.subtract(daystoWeekMonday, 'days');
        to = to.subtract(daystoWeekMonday, 'days').add('days', 6);
        break;
      case 'thismonth':
        from = moment([from.year(), from.month()]);
        to = moment(from).endOf('month');
        break;
      case 'lastcalendarmonth':
        from = from.subtract(1, 'month');
        from = moment([from.year(), from.month()]);
        to = moment(from).endOf('month');
        break;
      case 'thisquarter':
        from = from.quarter(from.quarter()).startOf('quarter');
        to = to.quarter(to.quarter()).endOf('quarter');
        break;
      case 'lastsixmonths':
        from = from.subtract(6, 'month');
        break;
      case 'thisyear':
        from = moment([from.year()]);
        to = moment(from).endOf('year');
        break;
      case 'lastyear':
        from = moment([from.year() - 1]);
        to = moment(from).endOf('year');
        break;
      case 'custom':
        from = moment(dateRangeFrom, 'MMM DD, YYYY');
        to = moment(dateRangeTo, 'MMM DD, YYYY');

        if (!from.isValid() || !to.isValid()) {
          this.setState({ errorMessage: 'The custom date range contains invalid dates, please correct and try again.' });
        }

        if (to < from) {
          this.setState({ errorMessage: 'The "from date" cannot be after the "to date" in your custom date range, please correct and try again.' });
        }
        break;
      default:
        return;
    }

    if (trendAnalysisType === null || typeof (trendAnalysisType) === 'undefined') {
      this.setState({ trendAnalysisType: 'none' });
      dispatch(getMiDashboard(clientId, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'), 'none', includeDeletedForms));
    } else {
      dispatch(getMiDashboard(clientId, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'), trendAnalysisType, includeDeletedForms));
    }
  }

  editDateRange() {
    this.openCustomDateRangeModal();
    this.setState({ selectedDatePeriod: 'custom', trendAnalysisType: 'none' });
  }

  checkDaysInCustomDateRange(trend) {
    const { dateRangeFrom, dateRangeTo } = this.props;

    const from = moment(dateRangeFrom, 'MMM DD, YYYY');
    const to = moment(dateRangeTo, 'MMM DD, YYYY');
    const diff = to.diff(from, 'days');

    let returnVal = false;
    if (diff <= 7 && trend === 'day') {
      returnVal = true;
    } else if (diff > 7 && diff <= 30 && (trend === 'week' || trend === 'day')) {
      return true;
    } else if (diff > 30 && diff <= 365 && (trend === 'week' || trend === 'month')) {
      return true;
    } else if (diff > 365 && (trend === 'year' || trend === 'month')) {
      return true;
    }
    return returnVal;
  }

  resetForm() {
    const { dispatch } = this.props;
    dispatch(resetMiDashboard());
  }

  isTrendOptionShown(trend) {
    const { selectedDatePeriod } = this.state;

    if (!selectedDatePeriod) {
      return false;
    }

    if (selectedDatePeriod === 'custom') {
      return this.checkDaysInCustomDateRange(trend);
    }

    switch (trend) {
      case 'day':
        return selectedDatePeriod.indexOf('week') > -1
          || (selectedDatePeriod.indexOf('month') > -1 && selectedDatePeriod.indexOf('sixmonths') === -1);
      case 'week':
        return selectedDatePeriod.indexOf('month') > -1
          || selectedDatePeriod.indexOf('year') > -1 || selectedDatePeriod.indexOf('quarter') > -1;
      case 'month':
        return selectedDatePeriod.indexOf('year') > -1
          || selectedDatePeriod.indexOf('sixmonths') > -1 || selectedDatePeriod.indexOf('quarter') > -1;
      case 'year':
        return false;
      default:
        return selectedDatePeriod.indexOf('day') === -1;
    }
  }

  hasData() {
    const { miData } = this.props;
    return miData != null && miData.total != null && miData.total > 0;
  }

  openCustomDateRangeModal() {
    const { dispatch } = this.props;
    dispatch(showCustomDateRangeModal());
  }

  cancelCustomDateRangeModal() {
    const { dispatch } = this.props;
    dispatch(hideCustomDateRangeModal('', ''));
  }

  applyCustomDateRangeModal(fromDate, toDate) {
    const { dispatch } = this.props;
    dispatch(hideCustomDateRangeModal(fromDate, toDate));
  }

  isReportingPeriodSelected() {
    const { dateRangeFrom, dateRangeTo } = this.props;
    const { selectedDatePeriod } = this.state;

    return (selectedDatePeriod !== '' && selectedDatePeriod !== 'custom')
      || (selectedDatePeriod === 'custom' && dateRangeFrom !== '' && dateRangeTo !== '');
  }

  CustomRangeOptionText() {
    const { dateRangeFrom, dateRangeTo } = this.props;
    const { selectedDatePeriod } = this.state;

    if (selectedDatePeriod === 'custom' && dateRangeFrom && dateRangeTo) {
      return `Custom Range ( ${ dateRangeFrom } to ${ dateRangeTo } )`;
    }
    return 'Custom Range';
  }

  render() {
    const {
      isDateRangePickerShown,
      isGettingMiDashboard,
      isGettingMiDashboardXls,
      miData,
      clients
    } = this.props;
    const { trendAnalysisType, errorMessage, selectedDatePeriod, clientId, disableSubmit, includeDeletedForms } = this.state;

    if (!this.hasData() || isGettingMiDashboard) {
      return (
        <div className="mi-dashboard">
          <div className="mi-dashboard-header">
            <h3>Welcome to the Management Information Dashboard</h3>
            <h4>
              Select from the drop downs the period &amp; trend for which you would like the report
            </h4>
          </div>
          <div className="form mi-content">
            <div className="form-group">
              <div className="col-4">
                <label htmlFor="client-select" className="form-label">Client</label>
              </div>
              <div className="col-6">
                <select
                  id="client-select"
                  className="form-select"
                  value={ clientId }
                  onChange={ e => this.onSelectedClientChange(e) }
                  disabled={ isGettingMiDashboard || (clients != null && clients.length === 1) }
                >
                  { clients != null && clients.map(client => client != null && <option key={ client.key } value={ client.key }>{ client.value }</option>) }
                </select>
              </div>
            </div>
            <div className="form-group">
              <div className="col-4">
                <label htmlFor="date-period-select" className="form-label">Validation Date Range </label>
              </div>
              <div className="col-6">
                <select
                  id="date-period-select"
                  className="form-select"
                  selected={ selectedDatePeriod }
                  onChange={ (event) => { this.onDatePeriodChange(event); } }
                  disabled={ isGettingMiDashboard }
                >
                  <option key="period" value="">Please select</option>
                  <option key="today" value="today">Today</option>
                  <option key="thisweek" value="thisweek">This Week</option>
                  <option key="thismonth" value="thismonth">This Month</option>
                  <option key="lastcalendarmonth" value="lastcalendarmonth">Last Calendar Month</option>
                  <option key="thisquarter" value="thisquarter">This Quarter</option>
                  <option key="lastsixmonths" value="lastsixmonths">Last 6 Months</option>
                  <option key="thisyear" value="thisyear">This Year</option>
                  <option key="custom" value="custom" onClick={ (event) => { this.onDatePeriodChange(event); } }>{ this.CustomRangeOptionText() }</option>
                </select>
              </div>
              { selectedDatePeriod === 'custom' && <div className="col-2">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={ () => this.editDateRange() }
                >
                  (Edit)
                </button>
              </div> }
            </div>
            <div className="form-group">
              <div className="col-4">
                <label htmlFor="trend-analysis-select" className="form-label">Trend Analysis</label>
              </div>
              <div className="col-6">
                <select
                  id="trend-analysis-select"
                  className="form-select"
                  selected={ trendAnalysisType }
                  onChange={ (event) => { this.onTrendAnalysisChange(event); } }
                  disabled={ isGettingMiDashboard }
                >
                  <option key="none" value="none">Do not include</option>
                  { this.isTrendOptionShown('day') && <option key="daily" value="day">Daily</option> }
                  { this.isTrendOptionShown('week') && <option key="week" value="week">Weekly</option> }
                  { this.isTrendOptionShown('month') && <option key="month" value="month">Monthly</option> }
                  { this.isTrendOptionShown('year') && <option key="year" value="year">Yearly</option> }
                </select>
              </div>
            </div>
            <div className="form-group">
              <div className="radio-button-group">
                <div className="col-4">
                  <label className="form-label">
                    Deleted Forms
                  </label>
                </div>
                <div className="col-6">
                  <label className="form-label-checkbox">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      id="include-deleted-forms"
                      name="includeDeletedForms"
                      value={ includeDeletedForms }
                      checked={ includeDeletedForms }
                      onChange={ (event) => { this.onIncludeDeleted(event); } }
                    />
                    <i className="form-icon" />
                    Include (as on Commercial MI)
                  </label>
                </div>
              </div>
              <div className="form-group submit-group">
                <button
                  type="submit"
                  className="btn btn-primary"
                  onClick={ this.onRunReportClick }
                  disabled={ isGettingMiDashboard || !this.isReportingPeriodSelected() || disableSubmit }
                >
                  {isGettingMiDashboard && <span className="icon loading" />}
                  Run Report
                </button>
              </div>
            </div>
            { (errorMessage) && <div>
              <div className="toast toast-error">
                <button
                  type="button"
                  className="btn btn-clear float-right"
                  onClick={ () => this.onCloseErrorMessage() }
                />
                { errorMessage }
              </div>
            </div> }
          </div>
          <MICustomDateRange
            isShown={ isDateRangePickerShown }
            onCancel={ () => this.cancelCustomDateRangeModal() }
            onClose={ this.applyCustomDateRangeModal }
          />
        </div>
      );
    } if (this.hasData() && !isGettingMiDashboard) {
      return (
        <MIResults
          data={ miData }
          trendAnalysisType={ trendAnalysisType }
          isGettingMiDashboardXls={ isGettingMiDashboardXls }
        />
      );
    }
    return '';
  }
}

function mapStateToProps(state) {
  let initialClientId = '';
  const {
    auth: {
      settings: {
        clients,
        selectedClient
      }
    },
    reports: {
      isDateRangePickerShown,
      dateRangeFrom,
      dateRangeTo,
      isGettingMiDashboard,
      isGettingMiDashboardXls,
      miData,
      error
    }
  } = state;

  if (state.auth.settings.selectedClient) {
    initialClientId = state.auth.settings.selectedClient.key;
  } else if (state.auth.settings.clients && state.auth.settings.clients.length === 1) {
    initialClientId = state.auth.settings.clients[0].id;
  }

  return {
    clients,
    selectedClient,
    isDateRangePickerShown,
    dateRangeFrom,
    dateRangeTo,
    isGettingMiDashboard,
    isGettingMiDashboardXls,
    miData,
    error,
    initialValues: {
      clientId: initialClientId,
    }
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(MIDashboard, 'MIDashboard'));
